// Created for use in budgets page remaining columns
// - displays value as currency
// - displays green percent bar if value is within limit
// - displays red percent bar if value is negative, using absolute value
// - caps bar at 100% if value exceeds limit

import { useMemo } from 'react';

import colors from '@atom/styles/colors';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';

interface Props {
  value: number;
  limit: number;
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '100%',
  },
  barContainer: {
    height: '0.5rem',
    borderRadius: '0.25rem',
    width: '100%',
  },
  bar: {
    height: '0.5rem',
    borderRadius: '0.25rem',
  },
};

const PercentBar = ({ value, limit }: Props) => {
  const [width, percentVal] = useMemo(() => {
    const absVal = Math.abs(value);
    const overflow = absVal > limit;
    return [
      overflow ? '100%' : `${(absVal / limit) * 100}%`,
      overflow ? 100 : Math.round((absVal / limit) * 100),
    ];
  }, [value, limit]);

  const isNegative = useMemo(() => value < 0, [value]);

  const backgroundColor = useMemo(
    () =>
      isNegative ? colors.percentBar.negativeBk : colors.percentBar.positiveBk,
    [isNegative],
  );

  const barColor = useMemo(
    () =>
      isNegative ? colors.percentBar.negative : colors.percentBar.positive,
    [isNegative],
  );

  const valueString = numberToLocaleString(value);
  const percentString = `(${isNegative ? '-' : ''}${percentVal}%)`;
  const displayString =
    value !== 0 ? `${valueString} ${percentString}` : valueString;

  return (
    <div style={styles.container}>
      <span style={styles.container}>{displayString}</span>
      <div style={{ ...styles.barContainer, background: backgroundColor }}>
        {value !== 0 && (
          <div style={{ ...styles.bar, width, background: barColor }} />
        )}
      </div>
    </div>
  );
};

export default PercentBar;
